import classNames from 'classnames';
import { IconEye, IconInfo, IconPrinter } from 'featherico';
import { compose, map, reject, values } from 'lodash/fp';
import PropTypes from 'prop-types';
import Avatar from '../../../../../components/Avatar';
import Button from '../../../../../components/Button';
import UserDateTime from '../../../../../components/DateTime/UserDateTime';
import Link from '../../../../../components/Link';
import LinkButton from '../../../../../components/LinkButton';
import { Option, Select } from '../../../../../components/Select';
import { usePrint } from '../../../../../hooks/usePrint';
import { trackEvent } from '../../../../../services/Tracking';
import EditLink from '../../../components/EditLink';
import { getViewUrl } from '../../../services/url';
import { ConfidentialQuestionToggle } from './ConfidentialQuestionToggle';
import styles from './style.m.less';

export default PageHeader;

export const VIEW_LAYOUTS = {
    STACKED: {
        type: 'STACKED',
        name: 'Stacked',
        layout: 'STACKED',
        showReviewee: true,
        showReviewer: true,
    },
    SIDE_BY_SIDE: {
        type: 'SIDE_BY_SIDE',
        name: 'Side by side',
        layout: 'COLUMNS',
        showReviewee: true,
        showReviewer: true,
    },
    REVIEWEE_ONLY: {
        type: 'REVIEWEE_ONLY',
        name: 'Reviewee only',
        layout: 'STACKED',
        showReviewee: true,
        showReviewer: false,
    },
    REVIEWER_ONLY: {
        type: 'REVIEWER_ONLY',
        name: 'Reviewer only',
        layout: 'STACKED',
        showReviewee: false,
        showReviewer: true,
    },
    HIDE_BOTH: {
        type: 'HIDE_BOTH',
        name: 'Hide both',
        layout: 'STACKED',
        showReviewee: false,
        showReviewer: false,
    },
};

PageHeader.propTypes = {
    title: PropTypes.string,
    user: PropTypes.object.isRequired,
    createdAt: PropTypes.string,
    completedAt: PropTypes.string,
    viewLayout: PropTypes.oneOf(values(VIEW_LAYOUTS)),
    onSelectViewLayout: PropTypes.func,
    confidentialQuestionToggle: PropTypes.shape({
        showConfidentialQuestions: PropTypes.bool,
        toggleConfidentialQuestions: PropTypes.func.isRequired,
        showConfidentialQuestionsButton: PropTypes.bool.isRequired,
    }),
};

function PageHeader({
    title,
    user,
    createdAt,
    completedAt,
    viewLayout,
    onSelectViewLayout,
    confidentialQuestionsToggle: {
        showConfidentialQuestions,
        toggleConfidentialQuestions,
        showConfidentialQuestionsButton,
    },
    showInfoAndTimeline,
    toggleInfoAndTimeline,
}) {
    return (
        <header className={styles.header}>
            <div className={styles.containerHeading}>
                <Avatar user={user} />
                <h1 className={styles.heading}>{title}</h1>
                <p className={styles.subTitle}>
                    <Date label="Created" date={createdAt} />
                    <Date label="Completed" date={completedAt} />
                </p>
            </div>
            <div className={styles.pageHeaderButtons}>
                {showConfidentialQuestionsButton && (
                    <ConfidentialQuestionToggle
                        showConfidentialQuestions={showConfidentialQuestions}
                        toggleConfidentialQuestions={toggleConfidentialQuestions}
                    />
                )}
                <ViewLayoutSelect viewLayout={viewLayout} onSelectViewLayout={onSelectViewLayout} />
                <PrintButton />
                <InfoAndTimelineToggle
                    toggleInfoAndTimeline={toggleInfoAndTimeline}
                    showInfoAndTimeline={showInfoAndTimeline}
                />
            </div>
        </header>
    );
}

const ViewLayoutSelect = ({ viewLayout, onSelectViewLayout }) => {
    const optionToText = layout => (layout ? layout.name : '');

    return onSelectViewLayout ? (
        <Select
            className={styles.viewLayoutSelect}
            selected={viewLayout}
            onSelect={selected => {
                trackEvent({ category: 'review title bar', action: 'select view layout', label: selected.name });
                return onSelectViewLayout(selected.type);
            }}
            optionToText={optionToText}
        >
            {compose(
                map(layout => (
                    <Option key={layout.type} value={layout}>
                        {layout.name}
                    </Option>
                )),
                reject(['type', 'HIDE_BOTH'])
            )(VIEW_LAYOUTS)}
        </Select>
    ) : (
        ''
    );
};

const PrintButton = () => {
    const print = usePrint();

    return (
        <Button
            className={styles.pageHeaderButton}
            onClick={print}
            eventTracking={{ category: 'review title bar', action: 'print' }}
        >
            <IconPrinter /> Print
        </Button>
    );
};

const InfoAndTimelineToggle = ({ toggleInfoAndTimeline, showInfoAndTimeline }) => (
    <Button
        onClick={toggleInfoAndTimeline}
        className={classNames(styles.pageHeaderButton, { done: showInfoAndTimeline })}
        eventTracking={{
            category: 'review title bar',
            action: 'toggle timeline',
            label: showInfoAndTimeline ? 'hide' : 'show',
        }}
    >
        <IconInfo /> Info &amp; Timeline
    </Button>
);

export function CardHeader({
    title,
    createdAt,
    completedAt,
    review,
    selfAssessment,
    mainReviewerAssessment,
    showPrintButton = true,
    isExportView = false,
}) {
    return (
        <header
            className={classNames(styles.header, {
                [styles.completeRibbonOffset]: review.complete,
            })}
        >
            <div>
                <h1 className={styles.heading}>
                    <Link className={styles.headingLink} href={getViewUrl(review.id)}>
                        {title}
                    </Link>
                </h1>
                <p className={styles.subTitle}>
                    <Date label="Created" date={createdAt} />
                    <Date label="Completed" date={completedAt} />
                </p>
            </div>
            <div>
                {showPrintButton && <PrintViewButton review={review} />}
                {!isExportView && (
                    <EditLink
                        review={review}
                        revieweeStatus={selfAssessment.assessmentStatus}
                        mainReviewerStatus={mainReviewerAssessment.assessmentStatus}
                        className={styles.cardHeaderButton}
                    />
                )}
            </div>
        </header>
    );
}

function PrintViewButton({ review: { id } }) {
    return (
        <LinkButton className={styles.cardHeaderButton} href={getViewUrl(id)}>
            <IconEye /> Print View
        </LinkButton>
    );
}

function Date({ label, date }) {
    return (
        !!date && (
            <span className={styles.date}>
                {label} on <UserDateTime date={date} format="fullDate" />
            </span>
        )
    );
}
